import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  GridLinkButton,
  EyeColumnV8,
  CellYesNoV8,
  CellYesNoEngV8,
} from 'motion-components';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  isActiveColumn,
  lastDateUserColumn,
  gridInactiveRowStyle,
  gridRowSelectedStyle,
  datetimeColumn,
} from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { Company, isAfisCyprus } from 'shared-pages/common/constants';
import { FileRepoLink } from 'shared-pages/components/filerepo-link';
import { calculateDistance } from 'shared-pages/utils/geo';
import { formatFloat } from 'shared-pages/utils/helpers';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
    collectionPointId,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('QrCode'), { id: row.id }),
        state: {
          fromQrCodesGrid: !isGridInForm,
          fromCollectionPoint: isGridInForm,
          collectionPointId,
        },
      });
    },
    [history, isGridInForm, collectionPointId],
  );

  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'declaredDate',
        header: t('Ημ/νία Δήλωσης'),
        columnName: t('Ημ/νία Δήλωσης'),
        accessorKey: 'declaredDate',
        orderByField: 'declaredDate',
        ...datetimeColumn(t),
      },
      {
        id: 'qrCode',
        header: t('QR code'),
        columnName: t('QR code'),
        accessorKey: 'qrCode',
        orderByField: 'qrCode',
        cell: ({ row }) => {
          const qrCode = row.original.qrCode;
          return (
            <a href={qrCode} target="_blank" rel="noopener noreferrer">
              {qrCode}
            </a>
          );
        },
        size: 20,
        meta: { type: 'text' },
      },
      // 👇 Show collection point only when not embedded in a form
      ...(!isGridInForm
        ? [
            {
              id: 'collectionPointId',
              header: t('Σημείο Συλλογής'),
              columnName: t('Σημείο Συλλογής'),
              accessorKey: 'collectionpoint.name',
              orderByField: 'collectionpoint.name',
              cell: ({ row }) => {
                const cp = row.original.collectionpoint;
                return (
                  <div className="text-start">
                    <span>
                      <Link to={generatePath(getRouteByName('Collectionpoint'), { id: cp?.id })}>
                        {cp?.name}
                      </Link>
                      &nbsp;
                      <span style={{ color: 'gray', fontWeight: 'bold' }}>
                        {row.original.collectionpoint?.code}
                      </span>
                    </span>
                    <div style={{ color: 'gray', fontSize: 'smaller' }}>
                      {cp?.addressLine1},&nbsp;
                      {cp?.addressArea},&nbsp;
                      {cp?.addressCity}, Τ.Κ.{cp?.postalCode}&nbsp;
                      {cp?.addressDistrict} {cp?.region?.name}
                    </div>
                  </div>
                );
              },

              size: 35,
              meta: { type: 'text' },
            },
          ]
        : []),
      ...(isAfisCyprus
        ? [
            {
              id: 'binSerialNo',
              header: t('Αριθμός Κάδου'),
              columnName: t('Αριθμός Κάδου'),
              accessorKey: 'binSerialNo',
              orderByField: 'binSerialNo',
              cell: ({ row }) => (
                <div className="text-center">
                  {row.original.binSerialNo !== '0' ? row.original.binSerialNo : '-'}
                </div>
              ),
              size: 20,
            },
          ]
        : []),
      {
        id: 'containerTypeId',
        header: t('Τύπος Συσκευασίας'),
        columnName: t('Τύπος Συσκευασίας'),
        accessorKey: 'containertype.name',
        orderByField: 'containertypeId',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'driverComments',
        header: t('Σχόλια Οδηγού'),
        columnName: t('Σχόλια Οδηγού'),
        accessorKey: 'driverComments',
        orderByField: 'driverComments',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'declaredByUser',
        header: t('Χρήστης Δήλωσης'),
        columnName: t('Χρήστης Δήλωσης'),
        accessorKey: 'declaredByUser',
        orderByField: 'declaredByUser',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'latitude',
        header: t('Θέση'),
        columnName: t('Θέση'),
        accessorKey: 'latitude',
        orderByField: 'latitude',
        cell: ({ row }) => {
          const latLong = () => {
            if (!row.original.latitude) return '-';
            const label = `${row.original.longitude}, ${row.original.latitude}`;
            return (
              <GridLinkButton
                onClick={() => {
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${row.original.latitude}%2C${row.original.longitude}`,
                    '_new',
                  );
                }}
              >
                {label}
              </GridLinkButton>
            );
          };
          return <div>{latLong()}</div>;
        },
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'collectionpoint.latitude',
        header: t('Θέση Σημείου'),
        columnName: t('Θέση Σημείου'),
        accessorKey: 'collectionpoint.latitude',
        orderByField: 'collectionpoint.latitude',
        cell: ({ row }) => {
          const latLong = () => {
            if (!row.original.collectionpoint.latitude) return '-';
            const label = `${row.original.collectionpoint.longitude}, ${row.original.collectionpoint.latitude}`;
            return (
              <GridLinkButton
                onClick={() => {
                  window.open(
                    `https://www.google.com/maps/search/?api=1&query=${row.original.collectionpoint.latitude}%2C${row.original.collectionpoint.longitude}`,
                    '_new',
                  );
                }}
              >
                {label}
              </GridLinkButton>
            );
          };
          // calculate distance between qr code and collection point
          // from latitude and longitude

          const meters =
            row.original.latitude &&
            row.original.longitude &&
            row.original.collectionpoint.latitude &&
            row.original.collectionpoint.longitude
              ? calculateDistance(
                  row.original.latitude,
                  row.original.longitude,
                  row.original.collectionpoint.latitude,
                  row.original.collectionpoint.longitude,
                )
              : undefined;
          const metersColorClass = meters && meters < 30 ? 'text-success' : 'text-danger';
          return (
            <div>
              <div>{latLong()}</div>
              {meters && (
                <div className={metersColorClass}>
                  {formatFloat(meters, 0, 0) ?? '-'}m απόσταση απο τον κάδο
                </div>
              )}
            </div>
          );
        },
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'comments',
        header: t('Σχόλια'),
        columnName: t('Σχόλια'),
        accessorKey: 'comments',
        orderByField: 'comments',
        cell: TableCellV8,
        size: 20,
        meta: { type: 'text' },
      },
      {
        id: 'photoFileId1',
        header: t('Φωτογραφίες'),
        columnName: t('Φωτογραφίες'),
        accessorKey: 'photoFileId1',
        orderByField: 'photoFileId1',
        cell: (props) => {
          const { row } = props;
          return (
            <>
              <div>
                {row.original.photo1 && (
                  <FileRepoLink
                    fileId={row.original.photo1.id}
                    fileName={row.original.photo1.fileName}
                  />
                )}
              </div>
              <div>
                {row.original.photo2 && (
                  <FileRepoLink
                    fileId={row.original.photo2.id}
                    fileName={row.original.photo2.fileName}
                  />
                )}
              </div>
            </>
          );
        },
        size: 25,
      },
      {
        id: 'isNewCollectionpoint',
        header: t('Είναι Νέο Σημείο'),
        columnName: t('Είναι Νέο Σημείο'),
        accessorKey: 'isNewCollectionpoint',
        orderByField: 'isNewCollectionpoint',
        cell: TableCellV8,
        size: 7,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
      },
      {
        id: 'isCollectionpointClosed',
        header: t('Το Σημείο Έκλεισε'),
        columnName: t('Το Σημείο Έκλεισε'),
        accessorKey: 'isCollectionpointClosed',
        orderByField: 'isCollectionpointClosed',
        cell: TableCellV8,
        size: 7,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
      },
      {
        id: 'containerReplaced',
        header: t('Αντικαταστάθηκε ο Κάδος'),
        columnName: t('Αντικαταστάθηκε ο Κάδος'),
        accessorKey: 'containerReplaced',
        orderByField: 'containerReplaced',
        cell: TableCellV8,
        size: 7,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
      },

      // isActiveColumn(t, i18n),
    ],
    [t, i18n.language, isGridInForm],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={tableRef}
          loading={loading}
          id="drivers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
